<template>
  <v-dialog v-model="dialogVisible" max-width="900px" max-height="90vh" persistent scrollable content-class="draggable-dialog">
    <v-card class="dialog-card">
      <div class="analytics-header py-4 px-8 draggable-header sticky-header" @mousedown="startDrag">
          <div style="flex: 1;">
            <div class="d-flex align-center">
              <span class="text-h6 header-title">Niveles analíticos</span>
              <v-spacer></v-spacer>
              <v-btn
                outlined
                color="primary"
                class="mr-2"
                small
                @click="closeDialog"
              >
                Cancelar
              </v-btn>
              <v-btn
                color="primary"
                small
                @click="applySelection"
              >
                Aplicar
              </v-btn>
            </div>
            <div class="text-subtitle-1 grey--text text--darken-2 font-weight-medium mt-1">
              {{ plan.name }} <span v-if="accountCode" class="grey--text text--darken-1">- Cuenta: {{ accountCode }}</span>
            </div>

            <!-- Tree structure showing selected levels - ALWAYS VISIBLE -->
            <div class="mt-3">
              <div class="text-caption grey--text text--darken-1 mb-1">Selección actual:</div>
              <div class="selected-tree">
                <div v-if="selectedLevelsTree.length === 0" class="text-body-2 grey--text text--lighten-1 font-italic pa-2">
                  Ningún nivel seleccionado
                </div>
                <div v-else>
                  <div v-for="(parentNode, idx) in selectedLevelsTree" :key="idx" class="tree-parent mb-1">
                    <div class="tree-parent-name">
                      <v-icon small color="grey darken-2" class="mr-1">mdi-folder-outline</v-icon>
                      <span class="font-weight-medium">{{ parentNode.name }}</span>
                      <span v-if="parentNode.isDirectlySelected" class="ml-2 text-caption grey--text">(seleccionado)</span>
                      <v-btn
                        v-if="parentNode.isDirectlySelected"
                        icon
                        small
                        color="red lighten-1"
                        class="ml-auto tree-remove-btn"
                        @click="removeParentSelection(parentNode.code)"
                      >
                        <v-icon small>mdi-close-box</v-icon>
                      </v-btn>
                    </div>
                    <div v-if="!parentNode.isDirectlySelected && parentNode.children.length > 0" class="tree-children ml-4 mt-1">
                      <div v-for="(child, childIdx) in parentNode.children" :key="childIdx" class="tree-child">
                        <v-icon x-small color="grey" class="mr-1">mdi-minus</v-icon>
                        <span class="text-body-2">{{ child.name }}</span>
                        <v-btn
                          icon
                          small
                          color="red lighten-1"
                          class="ml-auto tree-remove-btn"
                          @click="removeChildSelection(parentNode.code, child.code)"
                        >
                          <v-icon small>mdi-close-box</v-icon>
                        </v-btn>
                      </div>
                    </div>
                    <div v-else-if="!parentNode.isDirectlySelected && parentNode.children.length === 0" class="tree-children ml-4 mt-1">
                      <span class="text-body-2 grey--text text--lighten-1 font-italic">Sin selección</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
      </div>

      <v-card-text class="pb-4 pt-0 px-0">
        <v-divider class="mb-4"/>

          <!-- Toggle between company plan and predefined plan (only if predefined exists) -->
          <div v-if="hasPredefinedPlan" class="px-8 pt-4 pb-2 d-flex align-center">
            <v-btn-toggle
              v-model="usePredefinedPlan"
              mandatory
              dense
              class="plan-source-toggle"
            >
              <v-btn :value="false" small>
                <v-icon small left>mdi-office-building</v-icon>
                Plan Empresa
              </v-btn>
              <v-btn :value="true" small>
                <v-icon small left>mdi-file-document-outline</v-icon>
                Plan Cuenta
              </v-btn>
            </v-btn-toggle>

            <!-- Restore predefined plan button (only visible in Plan Cuenta mode) -->
            <v-btn
              v-if="usePredefinedPlan"
              small
              outlined
              color="primary"
              class="ml-2"
              @click="restorePredefinedPlan"
            >
              <v-icon small left>mdi-restore</v-icon>
              Restaurar plan predefinido
            </v-btn>
          </div>

          <!-- Dual search filters -->
          <div class="px-8" :class="hasPredefinedPlan ? 'pt-0' : 'pt-4'">
            <v-row dense>
              <v-col cols="6">
                <v-autocomplete
                  v-model="level1SearchFilter"
                  :items="level1Options"
                  item-text="text"
                  item-value="code"
                  return-object
                  prepend-inner-icon="mdi-magnify"
                  label="Seleccionar Nivel 1"
                  placeholder="Código o nombre de nivel 1"
                  clearable
                  dense
                  solo
                  flat
                  hide-details
                  class="search-field"
                >
                  <template v-slot:selection="{ item }">
                    {{ item.code }} - {{ item.name }}
                  </template>
                  <template v-slot:item="{ item }">
                    <v-list-item-content>
                      <v-list-item-title>{{ item.code }} - {{ item.name }}</v-list-item-title>
                    </v-list-item-content>
                  </template>
                </v-autocomplete>
              </v-col>
              <v-col cols="6">
                <v-text-field
                  v-model="level2SearchFilter"
                  prepend-inner-icon="mdi-magnify"
                  label="Buscar Nivel 2"
                  placeholder="Código o nombre de nivel 2"
                  clearable
                  dense
                  solo
                  flat
                  hide-details
                  class="search-field"
                ></v-text-field>
              </v-col>
            </v-row>
          </div>

          <!-- Flattened table showing all Level 2 items with their Level 1 parent info -->
          <div class="px-8 py-4">
            <!-- Flattened table with Level 1 and Level 2 columns -->
            <v-data-table
              :key="`flattened-${tableKey}`"
              :headers="flattenedTableHeaders"
              :items="paginatedFlattenedItems"
              :items-per-page="pageSize"
              class="level-selector-table flattened-table clickable-rows"
              dense
              fixed-header
              height="200px"
              hide-default-footer
              disable-sort
              @click:row="toggleRowSelection"
            >
              <!-- Checkbox column header - Select All -->
              <template v-slot:[`header.checkbox`]="">
                <v-checkbox
                  :input-value="allItemsSelected"
                  :indeterminate="someItemsSelected"
                  @click.stop="toggleAllSelection"
                  hide-details
                  dense
                  class="ma-0 pa-0"
                  title="Seleccionar todo / Deseleccionar todo"
                ></v-checkbox>
              </template>

              <!-- Checkbox column -->
              <template v-slot:[`item.checkbox`]="{ item }">
                <v-checkbox
                  v-model="item.ui_visible"
                  @change="handleFlattenedItemChange()"
                  @click.stop
                  hide-details
                  dense
                  class="ma-0 pa-0"
                ></v-checkbox>
              </template>

              <!-- Level 1 Code column -->
              <template v-slot:[`item.level1Code`]="{ item }">
                <span v-if="item._isStandaloneLevel1" class="grey--text text--darken-2">
                  {{ item.code }}
                </span>
                <span v-else class="grey--text text--darken-1">
                  {{ item._parent.code }}
                </span>
              </template>

              <!-- Level 1 Name column -->
              <template v-slot:[`item.level1Name`]="{ item }">
                <span v-if="item._isStandaloneLevel1" class="grey--text text--darken-2">
                  {{ item.name }}
                </span>
                <span v-else class="grey--text text--darken-1">
                  {{ item._parent.name }}
                </span>
              </template>

              <!-- Level 2 Code column -->
              <template v-slot:[`item.code`]="{ item }">
                <span v-if="item._isStandaloneLevel1" class="grey--text">—</span>
                <span v-else>{{ item.code }}</span>
              </template>

              <!-- Level 2 Name column -->
              <template v-slot:[`item.name`]="{ item }">
                <span v-if="item._isStandaloneLevel1" class="grey--text">Sin niveles 2</span>
                <span v-else>{{ item.name }}</span>
              </template>
            </v-data-table>

            <!-- Pagination and page size selector -->
            <div class="d-flex justify-end align-center mt-4 pagination-controls">
              <span class="mr-3">Filas por página:</span>
              <v-select
                v-model="pageSize"
                :items="pageSizeOptions"
                dense
                hide-details
                style="max-width: 60px"
                class="mr-5"
              ></v-select>
              <v-btn
                icon
                small
                :disabled="!hasPreviousPage"
                :class="{ 'pagination-btn-disabled': !hasPreviousPage }"
                @click="goToPreviousPage"
              >
                <v-icon>mdi-chevron-left</v-icon>
              </v-btn>
              <span class="mx-3">
                {{ startIndex }}-{{ endIndex }} de {{ filteredFlattenedLevel2Items.length }}
              </span>
              <v-btn
                icon
                small
                :disabled="!hasNextPage"
                :class="{ 'pagination-btn-disabled': !hasNextPage }"
                @click="goToNextPage"
              >
                <v-icon>mdi-chevron-right</v-icon>
              </v-btn>
            </div>

          </div>

      </v-card-text>

    </v-card>
  </v-dialog>
</template>

<script>
import { LineLevel } from "@/modules/analitic_accounting/domain/Company"

export default {
  name: 'AnalyticLevelSelectorPopup',
  props: {
    value: { type: Boolean, default: false },
    plan: { type: Object, required: true },
    showPercent: { type: Boolean, default: false },
    accountCode: { type: String, default: '' },
    company: { type: Object, default: null }
  },
  data() {
    return {
      tableKey: 0,
      treeKey: 0, // Force selectedLevelsTree to recalculate
      uiVisibleBackup: null,
      // Search filters for flattened view
      level1SearchFilter: null, // Changed to object for autocomplete
      level2SearchFilter: '',
      // Pagination
      currentPage: 0,
      pageSize: 10,
      pageSizeOptions: [10, 25, 50, 100],
      // Drag functionality
      isDragging: false,
      dragStartX: 0,
      dragStartY: 0,
      dialogX: 0,
      dialogY: 0,
      // Toggle between company plan and predefined account plan
      usePredefinedPlan: false,
      // Cached merged company plan levels (maintained as data to preserve references)
      cachedCompanyPlanLevels: [],
      // Cached merged predefined plan levels (maintained as data to preserve references)
      cachedPredefinedPlanLevels: []
    }
  },
  computed: {
    dialogVisible: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      }
    },
    hasPredefinedPlan() {
      // Check if the account has a predefined plan for this specific plan code
      if (!this.company || !this.company.accounts_plans || !this.accountCode || !this.plan) {
        return false;
      }

      const accountPlan = this.company.accounts_plans.find(ap => ap.code === this.accountCode);
      if (!accountPlan || !accountPlan.plans) {
        return false;
      }

      return accountPlan.plans.some(p => p.code === this.plan.code);
    },
    predefinedPlanLevels() {
      // Return cached merged predefined plan levels
      return this.cachedPredefinedPlanLevels;
    },
    companyPlanLevels() {
      // Return cached merged company plan levels
      return this.cachedCompanyPlanLevels;
    },
    parentLevels() {
      // Return either predefined plan levels or company plan levels based on toggle
      if (this.usePredefinedPlan && this.hasPredefinedPlan) {
        return this.predefinedPlanLevels;
      } else {
        return this.companyPlanLevels;
      }
    },
    level1Options() {
      // Get unique Level 1 items for autocomplete dropdown
      const options = [];

      if (!this.parentLevels || this.parentLevels.length === 0) {
        return options;
      }

      this.parentLevels.forEach(parent => {
        options.push({
          code: parent.code,
          name: parent.name || parent.code,
          text: `${parent.code} - ${parent.name || parent.code}` // For display
        });
      });

      return options;
    },
    flattenedLevel2Items() {
      // Flatten all Level 2 items from all Level 1 parents into a single array
      // Each item includes parent (Level 1) information
      // IMPORTANT: Return references to original objects, not copies, so ui_visible changes affect the source
      const flattened = [];

      if (!this.parentLevels || this.parentLevels.length === 0) {
        return flattened;
      }

      this.parentLevels.forEach(parent => {
        // If parent has no children, treat the parent itself as a selectable item
        if (!parent.levels || parent.levels.length === 0) {
          // Add metadata to the original parent object (don't create a copy)
          this.$set(parent, '_parent', null);
          this.$set(parent, '_isStandaloneLevel1', true);
          flattened.push(parent);
        } else {
          // Add all Level 2 children with parent reference
          parent.levels.forEach(child => {
            // Add metadata to the original child object (don't create a copy)
            this.$set(child, '_parent', {
              code: parent.code,
              name: parent.name
            });
            this.$set(child, '_isStandaloneLevel1', false);
            flattened.push(child);
          });
        }
      });

      return flattened;
    },
    filteredFlattenedLevel2Items() {
      // Apply Level 1 and Level 2 search filters
      let filtered = this.flattenedLevel2Items;

      // Filter by Level 1 autocomplete selection
      if (this.level1SearchFilter && this.level1SearchFilter.code) {
        const selectedCode = this.level1SearchFilter.code;
        filtered = filtered.filter(item => {
          if (item._isStandaloneLevel1) {
            // For standalone Level 1, match against itself
            return item.code === selectedCode;
          } else {
            // For Level 2 items, match against parent code
            return item._parent.code === selectedCode;
          }
        });
      }

      // Filter by Level 2 search
      if (this.level2SearchFilter && this.level2SearchFilter.trim() !== '') {
        const search2 = this.level2SearchFilter.toLowerCase().trim();
        filtered = filtered.filter(item => {
          const matchesCode = item.code && item.code.toLowerCase().includes(search2);
          const matchesName = item.name && item.name.toLowerCase().includes(search2);
          return matchesCode || matchesName;
        });
      }

      return filtered;
    },
    paginatedFlattenedItems() {
      // Paginate the filtered flattened items
      const startIndex = this.currentPage * this.pageSize;
      const endIndex = startIndex + this.pageSize;
      return this.filteredFlattenedLevel2Items.slice(startIndex, endIndex);
    },
    totalPages() {
      return Math.ceil(this.filteredFlattenedLevel2Items.length / this.pageSize);
    },
    hasPreviousPage() {
      return this.currentPage > 0;
    },
    hasNextPage() {
      return this.currentPage < this.totalPages - 1;
    },
    startIndex() {
      return this.currentPage * this.pageSize + 1;
    },
    endIndex() {
      const calculatedEnd = (this.currentPage + 1) * this.pageSize;
      return Math.min(calculatedEnd, this.filteredFlattenedLevel2Items.length);
    },
    flattenedTableHeaders() {
      // Headers for the flattened table view with Level 1 columns
      return [
        { text: '', value: 'checkbox', sortable: false, width: '50px', align: 'center' },
        { text: 'Código Nivel 1', value: 'level1Code', sortable: false, align: 'left', width: '120px' },
        { text: 'Nombre Nivel 1', value: 'level1Name', sortable: false, align: 'left', width: '180px' },
        { text: 'Código Nivel 2', value: 'code', sortable: false, align: 'left', width: '120px' },
        { text: 'Nombre Nivel 2', value: 'name', sortable: false, align: 'left' }
      ];
    },
    allItemsSelected() {
      // Check if all filtered items are selected
      const items = this.filteredFlattenedLevel2Items;
      if (items.length === 0) return false;
      return items.every(item => item.ui_visible === true);
    },
    someItemsSelected() {
      // Check if some (but not all) filtered items are selected
      const items = this.filteredFlattenedLevel2Items;
      if (items.length === 0) return false;
      const selectedCount = items.filter(item => item.ui_visible === true).length;
      return selectedCount > 0 && selectedCount < items.length;
    },
    selectedLevelsTree() {
      // Build a tree structure showing which Level 2 children are selected for each Level 1 parent
      // Also show Level 1 parents that are directly selected (when they have no children)
      // Force recalculation by depending on treeKey
      const _ = this.treeKey; // eslint-disable-line no-unused-vars
      const tree = [];

      // Always show selections from the original plan (this.plan.levels), not from parentLevels
      // This preserves the actual backend data and selections regardless of which view is active
      const levelsToShow = this.plan.levels || [];

      if (!levelsToShow || levelsToShow.length === 0) {
        return tree;
      }

      levelsToShow.forEach(parent => {
        const node = {
          code: parent.code,
          name: parent.name || parent.code,
          children: [],
          isDirectlySelected: false
        };

        // Check if this Level 1 has children
        const hasChildren = parent.levels && Array.isArray(parent.levels) && parent.levels.length > 0;

        if (hasChildren) {
          // Get selected children (Level 2s with ui_visible = true)
          // IMPORTANT: Only show if ui_visible is explicitly true AND not false
          parent.levels.forEach(child => {
            // Must be explicitly true, and never show if explicitly false
            if (child.ui_visible === true && child.ui_visible !== false) {
              node.children.push({
                code: child.code,
                name: child.name || child.code
              });
            }
          });
        } else {
          // No children: check if the Level 1 parent itself is selected
          // Must be explicitly true, and never show if explicitly false
          if (parent.ui_visible === true && parent.ui_visible !== false) {
            node.isDirectlySelected = true;
          }
        }

        // Only add to tree if:
        // 1. It has selected children, OR
        // 2. It's directly selected (Level 1 with no children)
        if (node.children.length > 0 || node.isDirectlySelected) {
          tree.push(node);
        }
      });

      return tree;
    }
  },
  watch: {
    value(newVal) {
      if (newVal) {
        this.initializeState();
      }
    },
    plan() {
      // Rebuild cached levels when plan prop changes (important for tests)
      this.buildCachedCompanyPlanLevels();
      this.buildCachedPredefinedPlanLevels();
    },
    level1SearchFilter() {
      // Reset to first page when search filter changes
      this.currentPage = 0;
    },
    level2SearchFilter() {
      // Reset to first page when search filter changes
      this.currentPage = 0;
    },
    usePredefinedPlan() {
      // Reset filters and pagination when switching between company and predefined plans
      this.level1SearchFilter = null;
      this.level2SearchFilter = '';
      this.currentPage = 0;

      // Rebuild cached levels based on which plan is active
      if (!this.usePredefinedPlan) {
        // Switching to company plan
        this.buildCachedCompanyPlanLevels();
      } else {
        // Switching to predefined plan
        this.buildCachedPredefinedPlanLevels();
      }

      this.tableKey++;
      this.treeKey++;
      console.log('Plan source toggled, resetting filters and refreshing view');
    }
  },
  methods: {
    initializeState() {
      console.log('Initializing state...');

      // Build cached company plan levels and predefined plan levels
      this.buildCachedCompanyPlanLevels();
      this.buildCachedPredefinedPlanLevels();

      // Auto-detect if we should show predefined plan or company plan
      // If the plan being edited matches the predefined plan structure, show predefined
      this.usePredefinedPlan = this.shouldDefaultToPredefinedPlan();

      // Initialize ui_visible for all children if not already set
      this.parentLevels.forEach((parent) => {
        if (parent.levels && parent.levels.length > 0) {
          parent.levels.forEach(child => {
            if (child.ui_visible === undefined || child.ui_visible === null) {
              console.log(`Initializing ui_visible for child ${child.code} to false`);
              this.$set(child, 'ui_visible', false);
            }
          });
        } else {
          // If no children, initialize parent's ui_visible
          if (parent.ui_visible === undefined || parent.ui_visible === null) {
            console.log(`Initializing ui_visible for parent ${parent.code} to false`);
            this.$set(parent, 'ui_visible', false);
          }
        }
      });

      // Create backup of ui_visible states
      this.createUiVisibleBackup();
    },
    handleFlattenedItemChange() {
      // Handler for checkbox changes in the flattened table view
      // Sync changes from merged structure to original plan in real-time
      if (!this.usePredefinedPlan) {
        this.syncCompanyPlanSelectionsToOriginalPlan();
      } else {
        this.syncPredefinedPlanSelectionsToOriginalPlan();
      }
      // Force re-render of the tree by incrementing treeKey
      this.treeKey++;
      console.log('Flattened item checkbox changed, tree refreshed');
    },
    toggleRowSelection(item) {
      // Toggle the checkbox when clicking anywhere on the row
      this.$set(item, 'ui_visible', !item.ui_visible);
      this.handleFlattenedItemChange();
    },
    toggleAllSelection() {
      // Toggle all filtered items selection
      const items = this.filteredFlattenedLevel2Items;
      const newValue = !this.allItemsSelected;

      items.forEach(item => {
        this.$set(item, 'ui_visible', newValue);
      });

      this.handleFlattenedItemChange();
    },
    goToNextPage() {
      if (this.hasNextPage) {
        this.currentPage++;
      }
    },
    goToPreviousPage() {
      if (this.hasPreviousPage) {
        this.currentPage--;
      }
    },
    applySelection() {
      console.log('blackraven: Aplicar clicked - Plan values BEFORE cleanup:', this.plan);

      // Sync selections back to original plan based on which view is active
      if (!this.usePredefinedPlan) {
        console.log('blackraven: Syncing from company plan to original plan...');
        this.syncCompanyPlanSelectionsToOriginalPlan();
      } else {
        console.log('blackraven: Syncing from predefined plan to original plan...');
        this.syncPredefinedPlanSelectionsToOriginalPlan();
      }

      // CRITICAL: Always work on this.plan.levels (not parentLevels) after syncing
      // This ensures amounts are set in the actual plan data that gets saved
      console.log('blackraven: Clearing and distributing on original plan...');
      this.clearHiddenLevelAmountsOnPlan(this.plan.levels);

      console.log('blackraven: Aplicar clicked - Plan values AFTER cleanup:', this.plan);

      // Auto-assign amounts equally to selected leaves
      this.autoDistributeAmountsOnPlan(this.plan.levels);

      console.log('blackraven: Final plan after distribution:', this.plan);
      this.$emit('apply');
      // Don't restore backup when applying - just close
      this.dialogVisible = false;
    },
    clearHiddenLevelAmounts() {
      // Clear amounts on the original plan levels (not cached/merged levels)
      // This ensures modifications are reflected in the original plan reference
      this.clearHiddenLevelAmountsOnPlan(this.plan.levels);
    },
    clearHiddenLevelAmountsOnPlan(levels) {
      // Recursive function to process all levels in the tree
      const processLevel = (level) => {
        // If this level is not visible, set its amount to null
        if (level.ui_visible === false && level.amount !== null) {
          console.log(`blackraven: Setting amount to null for hidden level: ${level.code} (was: ${level.amount})`);
          this.$set(level, 'amount', null);
        }

        // Process children recursively
        if (level.levels && level.levels.length > 0) {
          level.levels.forEach(child => processLevel(child));
        }
      };

      // Process all provided levels
      if (levels && levels.length > 0) {
        levels.forEach(parent => {
          // Check if parent has children
          if (parent.levels && parent.levels.length > 0) {
            // Parent has level 2 children - process them
            parent.levels.forEach(child => processLevel(child));
          } else {
            // No level 2 children - check the parent itself (level 1)
            processLevel(parent);
          }
        });
      }
    },
    autoDistributeAmounts() {
      // Distribute amounts on the original plan levels (not cached/merged levels)
      // This ensures modifications are reflected in the original plan reference
      this.autoDistributeAmountsOnPlan(this.plan.levels);
    },
    autoDistributeAmountsOnPlan(levels) {
      // Hierarchical distribution: parents split equally, then children split equally
      // Rule: "parents split equally if percentages are not defined"

      if (this.plan.total_amount === null || this.plan.total_amount === undefined) {
        console.log('blackraven: No total_amount to distribute');
        return;
      }

      const totalAmount = this.plan.total_amount;
      console.log('blackraven: Starting hierarchical distribution with total:', totalAmount);

      // Helper function to split amount in cents (ensures exact distribution)
      const splitAmountInCents = (amountCents, count) => {
        if (count === 0) return [];
        const baseShare = Math.floor(amountCents / count);
        const remainder = amountCents - (baseShare * count);

        const shares = [];
        for (let i = 0; i < count; i++) {
          // Distribute remainder cents to last items
          shares.push(baseShare + (i >= count - remainder ? 1 : 0));
        }
        return shares;
      };

      // Recursive function to distribute amounts hierarchically
      const distributeToLevel = (level, amountCents) => {
        // Set this level's amount (convert from cents to decimal)
        const levelAmount = amountCents / 100;
        this.$set(level, 'amount', parseFloat(levelAmount.toFixed(2)));
        console.log(`  - ${level.code}: ${levelAmount.toFixed(2)}`);

        // If has visible children, split amount among them
        if (level.levels && level.levels.length > 0) {
          const visibleChildren = level.levels.filter(child => child.ui_visible === true);

          if (visibleChildren.length > 0) {
            // Split parent amount equally among visible children
            const childShares = splitAmountInCents(amountCents, visibleChildren.length);

            visibleChildren.forEach((child, index) => {
              distributeToLevel(child, childShares[index]);
            });
          }
        }
      };

      // Get visible top-level parents from the provided levels
      const visibleParents = levels.filter(parent => {
        // Parent is visible if:
        // 1. It has no children and ui_visible = true, OR
        // 2. It has visible children
        if (!parent.levels || parent.levels.length === 0) {
          return parent.ui_visible === true;
        } else {
          return parent.levels.some(child => child.ui_visible === true);
        }
      });

      console.log('blackraven: Visible top-level parents:', visibleParents.length);

      if (visibleParents.length === 0) {
        console.log('blackraven: No visible parents to distribute to');
        return;
      }

      // Convert total amount to cents for precise distribution
      const totalCents = Math.round(totalAmount * 100);

      // Split total amount equally among visible top-level parents
      const parentShares = splitAmountInCents(totalCents, visibleParents.length);

      // Distribute to each parent and recursively to their children
      visibleParents.forEach((parent, index) => {
        console.log(`blackraven: Distributing to parent ${parent.code}:`);
        distributeToLevel(parent, parentShares[index]);
      });

      console.log('blackraven: Hierarchical distribution complete');
    },
    createUiVisibleBackup() {
      // Create a deep copy of ui_visible states
      this.uiVisibleBackup = {};

      const backupLevel = (level) => {
        // Store the ui_visible state using the level's code as key
        if (level.code) {
          this.uiVisibleBackup[level.code] = level.ui_visible;
        }

        // Recursively backup children
        if (level.levels && level.levels.length > 0) {
          level.levels.forEach(child => backupLevel(child));
        }
      };

      // Backup all parent levels
      this.parentLevels.forEach(parent => {
        backupLevel(parent);
      });

      console.log('blackraven: Created ui_visible backup:', this.uiVisibleBackup);
    },
    restoreUiVisibleBackup() {
      if (!this.uiVisibleBackup) {
        console.log('blackraven: No backup to restore');
        return;
      }

      const restoreLevel = (level) => {
        // Restore the ui_visible state from backup
        if (level.code && this.uiVisibleBackup.hasOwnProperty(level.code)) {
          const oldValue = level.ui_visible;
          const newValue = this.uiVisibleBackup[level.code];

          if (oldValue !== newValue) {
            console.log(`blackraven: Restoring ui_visible for ${level.code}: ${oldValue} -> ${newValue}`);
            this.$set(level, 'ui_visible', newValue);
          }
        }

        // Recursively restore children
        if (level.levels && level.levels.length > 0) {
          level.levels.forEach(child => restoreLevel(child));
        }
      };

      // Restore all parent levels
      this.parentLevels.forEach(parent => {
        restoreLevel(parent);
      });

      console.log('blackraven: Restored ui_visible states from backup');
    },
    startDrag(e) {
      // Don't drag if clicking on the close button
      if (e.target.closest('.close-button')) {
        return;
      }

      this.isDragging = true;
      const dialog = document.querySelector('.draggable-dialog');
      const rect = dialog.getBoundingClientRect();

      this.dragStartX = e.clientX - rect.left;
      this.dragStartY = e.clientY - rect.top;

      document.addEventListener('mousemove', this.drag);
      document.addEventListener('mouseup', this.stopDrag);

      // Prevent text selection while dragging
      e.preventDefault();
    },
    drag(e) {
      if (!this.isDragging) return;

      const dialog = document.querySelector('.draggable-dialog');
      if (!dialog) return;

      const newX = e.clientX - this.dragStartX;
      const newY = e.clientY - this.dragStartY;

      // Keep dialog within viewport bounds
      const maxX = window.innerWidth - dialog.offsetWidth;
      const maxY = window.innerHeight - dialog.offsetHeight;

      this.dialogX = Math.max(0, Math.min(newX, maxX));
      this.dialogY = Math.max(0, Math.min(newY, maxY));

      dialog.style.left = `${this.dialogX}px`;
      dialog.style.top = `${this.dialogY}px`;
    },
    stopDrag() {
      this.isDragging = false;
      document.removeEventListener('mousemove', this.drag);
      document.removeEventListener('mouseup', this.stopDrag);
    },
    removeParentSelection(code) {
      // Find the parent level by code in the original plan
      const parent = this.plan.levels.find(p => p.code === code);
      if (!parent) return;

      // Set ui_visible to false for the parent
      this.$set(parent, 'ui_visible', false);

      // Also update in merged structure based on which view is active
      if (!this.usePredefinedPlan) {
        const mergedParent = this.companyPlanLevels.find(p => p.code === code);
        if (mergedParent) {
          this.$set(mergedParent, 'ui_visible', false);
        }
      } else {
        const mergedParent = this.predefinedPlanLevels.find(p => p.code === code);
        if (mergedParent) {
          this.$set(mergedParent, 'ui_visible', false);
        }
      }

      // Force table and tree re-render
      this.tableKey++;
      this.treeKey++;
    },
    removeChildSelection(parentCode, childCode) {
      // Find the parent level by code in the original plan
      const parent = this.plan.levels.find(p => p.code === parentCode);
      if (!parent || !parent.levels) return;

      // Find the child level by code
      const child = parent.levels.find(c => c.code === childCode);
      if (!child) return;

      // Set ui_visible to false for the child
      this.$set(child, 'ui_visible', false);

      // Also update in merged structure based on which view is active
      if (!this.usePredefinedPlan) {
        const mergedParent = this.companyPlanLevels.find(p => p.code === parentCode);
        if (mergedParent && mergedParent.levels) {
          const mergedChild = mergedParent.levels.find(c => c.code === childCode);
          if (mergedChild) {
            this.$set(mergedChild, 'ui_visible', false);
          }
        }
      } else {
        const mergedParent = this.predefinedPlanLevels.find(p => p.code === parentCode);
        if (mergedParent && mergedParent.levels) {
          const mergedChild = mergedParent.levels.find(c => c.code === childCode);
          if (mergedChild) {
            this.$set(mergedChild, 'ui_visible', false);
          }
        }
      }

      // Force table and tree re-render
      this.tableKey++;
      this.treeKey++;
    },
    closeDialog() {
      // Restore original ui_visible states when canceling
      this.restoreUiVisibleBackup();
      this.dialogVisible = false;
    },
    mergeBackendSelectionsWithMasterPlan(masterLevels, backendLevels) {
      // Merge master plan structure with backend selections
      // Returns ALL levels from master plan, overlaying ui_visible and amounts from backend

      if (!masterLevels || masterLevels.length === 0) {
        return [];
      }

      return masterLevels.map(masterLevel => {
        // Find matching level in backend data by code
        const backendLevel = backendLevels.find(bl => bl.code === masterLevel.code);

        // Create merged level
        const mergedLevel = {
          code: masterLevel.code,
          name: masterLevel.name || masterLevel.code,
          amount: backendLevel ? backendLevel.amount : null,
          ui_visible: backendLevel ? backendLevel.ui_visible : false,
          levels: []
        };

        // Recursively merge children
        if (masterLevel.levels && masterLevel.levels.length > 0) {
          mergedLevel.levels = this.mergeBackendSelectionsWithMasterPlan(
            masterLevel.levels,
            backendLevel && backendLevel.levels ? backendLevel.levels : []
          );
        }

        return mergedLevel;
      });
    },
    syncCompanyPlanSelectionsToOriginalPlan() {
      // Copy ui_visible selections from merged company plan structure back to original plan
      console.log('blackraven: Syncing company plan selections to original plan');

      const syncRecursive = (mergedLevels, originalLevels) => {
        if (!mergedLevels || !originalLevels) return;

        mergedLevels.forEach(mergedLevel => {
          // Find matching level in original by code, or create it if it doesn't exist
          let originalLevel = originalLevels.find(ol => ol.code === mergedLevel.code);

          if (!originalLevel) {
            // Level doesn't exist in original - create a proper LineLevel instance
            console.log(`  - Creating new LineLevel for ${mergedLevel.code}`);
            originalLevel = new LineLevel(mergedLevel.code, mergedLevel.name, [], null);
            originalLevel.ui_visible = false;
            originalLevels.push(originalLevel);
          }

          // Sync ui_visible
          if (mergedLevel.ui_visible !== originalLevel.ui_visible) {
            console.log(`  - Syncing ${mergedLevel.code}: ui_visible = ${mergedLevel.ui_visible}`);
            this.$set(originalLevel, 'ui_visible', mergedLevel.ui_visible);
          }

          // Recursively sync children
          if (mergedLevel.levels && mergedLevel.levels.length > 0) {
            if (!originalLevel.levels) {
              this.$set(originalLevel, 'levels', []);
            }
            syncRecursive(mergedLevel.levels, originalLevel.levels);
          }
        });
      };

      syncRecursive(this.companyPlanLevels, this.plan.levels);
      console.log('blackraven: Finished syncing selections');
    },
    syncPredefinedPlanSelectionsToOriginalPlan() {
      // Copy ui_visible selections from merged predefined plan structure back to original plan
      console.log('blackraven: Syncing predefined plan selections to original plan');

      const syncRecursive = (mergedLevels, originalLevels) => {
        if (!mergedLevels || !originalLevels) return;

        mergedLevels.forEach(mergedLevel => {
          // Find matching level in original by code, or create it if it doesn't exist
          let originalLevel = originalLevels.find(ol => ol.code === mergedLevel.code);

          if (!originalLevel) {
            // Level doesn't exist in original - create a proper LineLevel instance
            console.log(`  - Creating new LineLevel for ${mergedLevel.code}`);
            originalLevel = new LineLevel(mergedLevel.code, mergedLevel.name, [], null);
            originalLevel.ui_visible = false;
            originalLevels.push(originalLevel);
          }

          // Sync ui_visible
          if (mergedLevel.ui_visible !== originalLevel.ui_visible) {
            console.log(`  - Syncing ${mergedLevel.code}: ui_visible = ${mergedLevel.ui_visible}`);
            this.$set(originalLevel, 'ui_visible', mergedLevel.ui_visible);
          }

          // Recursively sync children
          if (mergedLevel.levels && mergedLevel.levels.length > 0) {
            if (!originalLevel.levels) {
              this.$set(originalLevel, 'levels', []);
            }
            syncRecursive(mergedLevel.levels, originalLevel.levels);
          }
        });
      };

      syncRecursive(this.predefinedPlanLevels, this.plan.levels);
      console.log('blackraven: Finished syncing predefined selections');
    },
    buildCachedCompanyPlanLevels() {
      // Build the merged company plan levels and cache them
      if (!this.company || !this.company.plans) {
        this.cachedCompanyPlanLevels = this.plan.levels || [];
        return;
      }

      const masterPlan = this.company.plans.find(p => p.code === this.plan.code);
      if (!masterPlan || !masterPlan.levels) {
        this.cachedCompanyPlanLevels = this.plan.levels || [];
        return;
      }

      // Merge master plan structure with backend selections
      this.cachedCompanyPlanLevels = this.mergeBackendSelectionsWithMasterPlan(
        masterPlan.levels,
        this.plan.levels || []
      );

      console.log('blackraven: Built cached company plan levels', this.cachedCompanyPlanLevels);
    },
    buildCachedPredefinedPlanLevels() {
      // Build the merged predefined plan levels and cache them
      if (!this.hasPredefinedPlan) {
        this.cachedPredefinedPlanLevels = [];
        return;
      }

      // Get the predefined plan template from company configuration
      const accountPlan = this.company.accounts_plans.find(ap => ap.code === this.accountCode);
      const predefinedPlan = accountPlan.plans.find(p => p.code === this.plan.code);

      if (!predefinedPlan || !predefinedPlan.levels) {
        this.cachedPredefinedPlanLevels = [];
        return;
      }

      // Merge predefined plan template with backend selections
      this.cachedPredefinedPlanLevels = this.mergeBackendSelectionsWithMasterPlan(
        predefinedPlan.levels,
        this.plan.levels || []
      );

      console.log('blackraven: Built cached predefined plan levels', this.cachedPredefinedPlanLevels);
    },
    shouldDefaultToPredefinedPlan() {
      // Determine if we should default to showing the predefined plan view
      // Logic:
      // - Check if current plan structure (parent-child relationships) matches predefined plan
      // - If structure differs (e.g., COM child of BAR vs COM child of MAD), reference was applied

      if (!this.hasPredefinedPlan) {
        console.log('blackraven: No predefined plan available, defaulting to company plan');
        return false;
      }

      // Get the ORIGINAL predefined plan template (not the merged/cached version)
      const accountPlan = this.company.accounts_plans.find(ap => ap.code === this.accountCode);
      if (!accountPlan || !accountPlan.plans) {
        console.log('blackraven: No account plan found, defaulting to company plan');
        return false;
      }

      const predefinedPlanTemplate = accountPlan.plans.find(p => p.code === this.plan.code);
      if (!predefinedPlanTemplate || !predefinedPlanTemplate.levels) {
        console.log('blackraven: No predefined plan template found, defaulting to company plan');
        return false;
      }

      // Extract parent-child relationships from both plans
      const currentRelationships = this.extractParentChildRelationships(this.plan.levels || []);
      const predefinedRelationships = this.extractParentChildRelationships(predefinedPlanTemplate.levels);

      console.log('blackraven: Current relationships:', Array.from(currentRelationships));
      console.log('blackraven: Predefined relationships:', Array.from(predefinedRelationships));

      // If current plan is empty, default to company plan
      if (currentRelationships.size === 0) {
        console.log('blackraven: Current plan is empty, defaulting to company plan');
        return false;
      }

      // Check if any relationship in current plan does NOT exist in predefined plan
      // This detects when structure differs (e.g., BAR>COM vs MAD>COM)
      const hasInvalidRelationship = Array.from(currentRelationships).some(rel => !predefinedRelationships.has(rel));
      if (hasInvalidRelationship) {
        console.log('blackraven: Reference was applied (structure mismatch), defaulting to company view');
        return false;
      }

      // All relationships match predefined plan structure
      console.log('blackraven: Structure matches predefined plan, defaulting to predefined view');
      return true;
    },
    extractParentChildRelationships(levels) {
      // Extract parent-child relationships as strings like "PARENT>CHILD"
      // Also includes standalone parents as "PARENT" (no children)
      const relationships = new Set();

      const traverse = (parent, parentCode = null) => {
        if (!parent) return;

        const currentCode = parent.code;

        // If this node has a parent, record the relationship
        if (parentCode) {
          relationships.add(`${parentCode}>${currentCode}`);
        } else {
          // Top-level node without children
          if (!parent.levels || parent.levels.length === 0) {
            relationships.add(currentCode);
          }
        }

        // Recursively process children
        if (parent.levels && parent.levels.length > 0) {
          parent.levels.forEach(child => traverse(child, currentCode));
        }
      };

      if (levels && levels.length > 0) {
        levels.forEach(level => traverse(level));
      }

      return relationships;
    },
    extractAllLevelCodes(levels) {
      // Recursively extract all level codes from a tree structure
      const codes = new Set();

      const traverse = (levelList) => {
        if (!levelList || levelList.length === 0) return;

        levelList.forEach(level => {
          if (level && level.code) {
            codes.add(level.code);
          }
          if (level && level.levels && level.levels.length > 0) {
            traverse(level.levels);
          }
        });
      };

      traverse(levels);
      return codes;
    },
    restorePredefinedPlan() {
      // Restore the original predefined plan configuration
      console.log('blackraven: Restoring predefined plan to original state');

      if (!this.hasPredefinedPlan) {
        console.log('blackraven: No predefined plan available to restore');
        return;
      }

      // Get the original predefined plan template
      const accountPlan = this.company.accounts_plans.find(ap => ap.code === this.accountCode);
      const predefinedPlan = accountPlan.plans.find(p => p.code === this.plan.code);

      if (!predefinedPlan || !predefinedPlan.levels) {
        console.log('blackraven: No predefined plan template found');
        return;
      }

      // Clear the current plan levels
      this.plan.levels.splice(0, this.plan.levels.length);

      // Deep clone the predefined plan levels and convert to LineLevel instances
      const clonePredefinedLevels = (templateLevels) => {
        if (!templateLevels || templateLevels.length === 0) return [];

        return templateLevels.map(templateLevel => {
          const lineLevel = new LineLevel(
            templateLevel.code,
            templateLevel.name,
            clonePredefinedLevels(templateLevel.levels || []),
            null // Start with null amount
          );
          // Set ui_visible to true for all predefined levels
          lineLevel.ui_visible = true;
          return lineLevel;
        });
      };

      // Add the cloned predefined levels to the plan
      const restoredLevels = clonePredefinedLevels(predefinedPlan.levels);
      restoredLevels.forEach(level => {
        this.plan.levels.push(level);
      });

      console.log('blackraven: Restored plan levels:', this.plan.levels);

      // Apply automatic distribution if configured
      if (this.plan.total_amount !== null && this.plan.total_amount !== undefined) {
        this.autoDistributeAmountsOnPlan(this.plan.levels);
      }

      // Rebuild cached structures
      this.buildCachedPredefinedPlanLevels();

      // Recreate backup
      this.createUiVisibleBackup();

      // Force refresh
      this.tableKey++;
      this.treeKey++;

      console.log('blackraven: Predefined plan restored successfully');
    }
  },
  mounted() {
    this.initializeState();
  },
  beforeDestroy() {
    // Clean up event listeners
    document.removeEventListener('mousemove', this.drag);
    document.removeEventListener('mouseup', this.stopDrag);
  }
}
</script>

<style scoped>
/* Draggable dialog */
::v-deep .draggable-dialog {
  position: fixed !important;
  margin: 0 !important;
}

::v-deep .draggable-dialog .v-card {
  pointer-events: all;
}

/* Draggable header */
.draggable-header {
  cursor: move;
  user-select: none;
}

/* Tree structure styles */
.selected-tree {
  background-color: rgba(102, 53, 155, 0.05);
  border-radius: 4px;
  padding: 8px 12px;
  max-height: 200px;
  overflow-y: auto;
}

.tree-parent {
  line-height: 1.5;
}

.tree-parent-name {
  display: flex;
  align-items: center;
  color: var(--v-primaryDark-base);
}

.tree-children {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.tree-child {
  display: flex;
  align-items: center;
  color: #5a5a5a;
}

/* Hide dialog scrollbar */
::v-deep .v-dialog.v-dialog--scrollable .v-card__text {
  scrollbar-width: none; /* Firefox */
  -ms-overflow-style: none; /* IE and Edge */
}

::v-deep .v-dialog.v-dialog--scrollable .v-card__text::-webkit-scrollbar {
  display: none; /* Chrome, Safari, Opera */
}

/* Header with specific colors */
.analytics-header {
  background-color: #FFFFFF !important;
}

/* Sticky header that stays visible when scrolling */
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 10;
  background-color: #FFFFFF !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.header-title {
  color: var(--v-primary-base) !important;
  font-weight: 700;
}

.close-button {
  color: var(--v-primaryDark-base) !important;
}

::v-deep .close-button .v-icon {
  font-size: 20px !important;
}

/* Two column layout */
.two-column-layout {
  display: flex;
  gap: 16px;
}

.two-column-layout .column-table {
  flex: 1;
  width: 50%;
}

.level-selector-table {
  border: 1px solid #e0e0e0;
}

::v-deep .level-selector-table.v-data-table > .v-data-table__wrapper {
  max-height: 200px;
}

/* v-data-table header styles */
::v-deep .level-selector-table.v-data-table thead th {
  background-color: var(--v-primaryLighter-base) !important;
  color: var(--v-primaryDark-base) !important;
  font-weight: bold !important;
}

/* Clickable rows styling */
::v-deep .clickable-rows tbody tr {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

::v-deep .clickable-rows tbody tr:hover {
  background-color: rgba(102, 53, 155, 0.08) !important;
}

/* Show scrollbar for two-column layout */
::v-deep .level-selector-table.show-scrollbar .v-data-table__wrapper {
  overflow-y: auto !important;
}

.divider-subtle {
  opacity: 0.2;
}

/* Tab text color */
::v-deep .v-tab {
  color: #565656 !important;
}

/* Search field styles (applies to both text field and autocomplete) */
::v-deep .search-field.v-text-field--solo .v-input__control,
::v-deep .search-field.v-autocomplete--solo .v-input__control {
  background-color: #F9F9F9 !important;
  border-radius: 8px !important;
}

::v-deep .search-field .v-input__slot {
  background-color: var(--v-neutral-base) !important;
  box-shadow: none !important;
  border-radius: 8px !important;
  padding-left: 20px !important;
}

::v-deep .search-field .v-icon {
  background-color: var(--v-primary-base) !important;
  color: #FFFFFF !important;
  border-radius: 8px !important;
  padding: 6px !important;
  width: 36px !important;
  height: 36px !important;
  margin-right: 16px !important;
}

::v-deep .search-field .v-text-field__slot {
  padding-left: 20px !important;
}

/* Pagination controls */
.pagination-controls {
  padding: 8px 0;
}

.pagination-controls span {
  font-size: 14px;
}

.pagination-btn-disabled {
  opacity: 0.3;
}

/* Flattened table styles */
.flattened-table {
  border: 1px solid #e0e0e0;
}

::v-deep .flattened-table.v-data-table > .v-data-table__wrapper {
  max-height: 200px;
}

::v-deep .flattened-table.v-data-table thead th {
  background-color: #E7DBF9 !important;
  color: #351457 !important;
  font-weight: bold !important;
  font-size: 0.75rem !important;
}

::v-deep .flattened-table.v-data-table tbody tr:hover {
  background-color: rgba(102, 53, 155, 0.05) !important;
}

.pagination-controls span {
  font-size: 14px;
}

/* Pagination button styles */
.pagination-btn-disabled .v-icon {
  color: #D1D1D1 !important;
}

/* Tab navigation arrows - always visible */
::v-deep .v-tabs .v-slide-group__prev,
::v-deep .v-tabs .v-slide-group__next {
  display: flex !important;
  min-width: 32px !important;
  background: transparent !important;
  box-shadow: none !important;
}

/* Tab arrows when enabled - darker color */
::v-deep .v-tabs .v-slide-group__prev:not(.v-slide-group__prev--disabled) .v-icon,
::v-deep .v-tabs .v-slide-group__next:not(.v-slide-group__next--disabled) .v-icon {
  color: #6D6D6D !important;
}

/* Tab arrows when disabled - lighter color */
::v-deep .v-tabs .v-slide-group__prev--disabled .v-icon,
::v-deep .v-tabs .v-slide-group__next--disabled .v-icon {
  color: #D1D1D1 !important;
}

/* Checkbox color when checked */
::v-deep .v-input--selection-controls.v-input--is-label-active .v-icon,
::v-deep .v-input--checkbox .v-input--selection-controls__input--is-dirty .v-icon {
  color: var(--v-primaryDark-base) !important;
}

/* Plan source toggle button styling */
.plan-source-toggle {
  border-radius: 8px;
  overflow: hidden;
}

::v-deep .plan-source-toggle .v-btn {
  text-transform: none;
  font-weight: 500;
  letter-spacing: normal;
}

::v-deep .plan-source-toggle .v-btn--active {
  background-color: var(--v-primary-base) !important;
  color: white !important;
}

::v-deep .plan-source-toggle .v-btn--active .v-icon {
  color: white !important;
}

::v-deep .plan-source-toggle .v-btn:not(.v-btn--active) {
  background-color: #F5F5F5 !important;
  color: #6D6D6D !important;
}

::v-deep .plan-source-toggle .v-btn:not(.v-btn--active) .v-icon {
  color: #6D6D6D !important;
}
</style>
